<template>
  <div>
    <div>
      <v-breadcrumbs
        class="px-0"
        :items="breadcrumbItems"
      />
    </div>
    <page-title :value="pageTitle" />

    <v-progress-linear v-if="isFetching" indeterminate color="primary" />

    <div v-if="detail" class="my-4">
      <div class="d-flex align-center flex-wrap" style="gap: 1rem;">
        <span class="text-h6 font-weight-bold">
          {{ detail.lastName }} {{ detail.firstName }}
        </span>
        <span class="text-caption text-grey">
          {{ detail.className }} / {{ detail.ageLabel }}
        </span>
        <ai-report-status-chip :status="detail.status" />
      </div>

      <div class="d-flex align-center flex-wrap mt-2 text-body-2 text-grey" style="gap: 1rem;">
        <span>対象期間: {{ detail.monthsLabel }}</span>
        <span :class="{ 'text-red': detail.dataCoverageMonths.length < 3 }">
          発達記録の対象月: {{ formatCoverage(detail.dataCoverageMonths) }}
          <span v-if="detail.dataCoverageMonths.length < 3">
            (一部の月のデータのみで生成)
          </span>
        </span>
        <span>生成回数: {{ detail.generationCount }} 回</span>
      </div>

      <!-- リロード後など、再生成がサーバ側で進行中のときの復元表示 (ダイアログ外) -->
      <v-alert
        v-if="isRegenerating && !regenerateDialogOpen"
        variant="outlined"
        color="primary"
        icon="mdi-progress-clock"
        density="compact"
        class="mt-3"
      >
        <div class="d-flex align-center flex-wrap" style="gap: 1rem;">
          <span class="text-body-2">
            再生成中です... ({{ activeBatch ? activeBatch.successCount : 0 }} / {{ activeBatch ? activeBatch.totalCount : 0 }})
          </span>
          <v-progress-linear
            indeterminate
            color="primary"
            height="6"
            rounded
            style="max-width: 240px;"
          />
        </div>
      </v-alert>

      <v-row class="mt-4">
        <v-col cols="12" md="8">
          <v-card variant="outlined" class="pa-5">
            <div class="d-flex align-center mb-3">
              <img
                src="@/assets/vevo/mascot_peek.png"
                alt="VEVO"
                class="mr-2"
                style="width: 28px; height: 28px; object-fit: contain;"
              />
              <span class="text-subtitle-1 font-weight-bold">レポート本文</span>
              <v-spacer />
              <span class="text-caption text-grey" v-if="detail.content">
                {{ contentLength }} 文字
              </span>
            </div>

            <div v-if="detail.content" class="ai-report-content text-body-1">
              {{ detail.content }}
            </div>
            <v-alert
              v-else
              type="info"
              variant="outlined"
              density="compact"
            >
              レポートはまだ生成されていません。クォーター詳細から一括生成、もしくは下の「生成」ボタンを押してください。
            </v-alert>

            <v-divider class="my-4" />

            <div class="d-flex align-center flex-wrap" style="gap: 0.5rem;">
              <v-btn
                v-if="detail.status === 'draft'"
                color="primary"
                variant="flat"
                :loading="isPublishing"
                @click="setStatus('published')"
              >
                <v-icon start>mdi-publish</v-icon>
                公開する
              </v-btn>
              <v-btn
                v-if="detail.status === 'published'"
                variant="outlined"
                :loading="isPublishing"
                @click="setStatus('draft')"
              >
                <v-icon start>mdi-undo</v-icon>
                下書きに戻す
              </v-btn>
              <v-tooltip
                v-if="detail.status === 'draft'"
                :disabled="!noData"
                location="top"
                text="対象期間に発達記録がないため生成できません"
              >
                <template v-slot:activator="{ props }">
                  <span v-bind="props">
                    <v-btn
                      variant="outlined"
                      :disabled="noData || isRegenerating"
                      @click="regenerateDialogOpen = true"
                    >
                      <v-icon start>mdi-refresh</v-icon>
                      再生成
                    </v-btn>
                  </span>
                </template>
              </v-tooltip>
              <v-tooltip
                v-if="detail.status === 'notGenerated'"
                :disabled="!noData"
                location="top"
                text="対象期間に発達記録がないため生成できません"
              >
                <template v-slot:activator="{ props }">
                  <span v-bind="props">
                    <v-btn
                      color="primary"
                      variant="outlined"
                      :disabled="noData || isRegenerating"
                      @click="regenerateDialogOpen = true"
                    >
                      <v-icon start>mdi-auto-fix</v-icon>
                      生成
                    </v-btn>
                  </span>
                </template>
              </v-tooltip>
            </div>

            <v-alert
              v-if="detail.status === 'published'"
              density="compact"
              variant="outlined"
              type="warning"
              class="mt-3 text-body-2"
            >
              公開済のため一括再生成ではスキップされます。再生成する場合は先に「下書きに戻す」を実行してください。
            </v-alert>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card variant="outlined" class="pa-5">
            <div class="text-subtitle-2 font-weight-bold text-grey-darken-2 mb-2">
              操作ログ
            </div>
            <div class="text-body-2 mb-1">
              <span class="text-grey">最終生成:</span>
              {{ formatDateTime(detail.lastGeneratedAt) }}
              <span v-if="detail.lastGeneratedBy" class="text-grey">
                / by {{ detail.lastGeneratedBy }}
              </span>
            </div>
            <div class="text-body-2 mb-1">
              <span class="text-grey">最終公開:</span>
              {{ formatDateTime(detail.lastPublishedAt) }}
              <span v-if="detail.lastPublishedBy" class="text-grey">
                / by {{ detail.lastPublishedBy }}
              </span>
            </div>
            <v-divider class="my-3" />
            <div class="text-subtitle-2 font-weight-bold text-grey-darken-2 mb-2">
              直近の再生成
            </div>
            <div v-if="detail.additionalPrompt" class="text-body-2">
              {{ detail.additionalPrompt }}
            </div>
            <div v-else class="text-body-2 text-grey">
              翻訳なし (通常生成)
            </div>
          </v-card>

          <v-card variant="outlined" class="pa-4 mt-3">
            <div class="text-subtitle-2 font-weight-bold text-grey-darken-2 mb-2">
              ステータスの説明
            </div>
            <div class="d-flex flex-column" style="gap: 0.5rem;">
              <div class="d-flex align-center" style="gap: 0.5rem;">
                <ai-report-status-chip status="notGenerated" />
                <span class="text-body-2">生成されていない (保護者には非表示)</span>
              </div>
              <div class="d-flex align-center" style="gap: 0.5rem;">
                <ai-report-status-chip status="draft" />
                <span class="text-body-2">生成済だが未公開 (保護者には非表示)</span>
              </div>
              <div class="d-flex align-center" style="gap: 0.5rem;">
                <ai-report-status-chip status="published" />
                <span class="text-body-2">保護者アプリから閲覧できる</span>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- 再生成ダイアログ -->
    <v-dialog v-model="regenerateDialogOpen" max-width="540" persistent>
      <v-card class="pa-5">
        <page-title :value="regenerateDialogTitle" />

        <p class="mt-3 text-body-2">
          {{ detail ? `${detail.lastName} ${detail.firstName}` : "" }} さんのレポートを{{ detail && detail.status === "notGenerated" ? "生成" : "再生成" }}します。
        </p>

        <v-alert variant="outlined" color="primary" icon="mdi-information-outline" density="compact" class="mt-2">
          <ul class="ma-0 pl-4 text-body-2">
            <li v-if="regenerateMode === 'translate'">現在のレポート本文を、選択した言語に翻訳して上書きします。</li>
            <li>非同期処理のため完了まで数十秒〜数分かかります。</li>
            <li>完了後、ステータスは <strong>下書き</strong> に戻ります。</li>
          </ul>
        </v-alert>

        <!-- 翻訳は本文がある下書きのみ選択可。未作成は通常生成のみ。 -->
        <template v-if="canTranslate">
          <v-radio-group
            v-model="regenerateMode"
            class="mt-3"
            density="compact"
            hide-details
            :disabled="!!activeBatch && activeBatch.status === 'processing'"
          >
            <v-radio label="単純な再生成 (発達記録から作り直す)" value="simple" />
            <v-radio label="翻訳 (現在の本文を選択言語に翻訳)" value="translate" />
          </v-radio-group>

          <v-select
            v-if="regenerateMode === 'translate'"
            v-model="selectedLanguage"
            :items="languageItems"
            item-title="label"
            item-value="code"
            label="翻訳先の言語"
            variant="outlined"
            density="compact"
            class="mt-3"
            :disabled="!!activeBatch && activeBatch.status === 'processing'"
          />
        </template>

        <div v-if="activeBatch" class="mt-2">
          <v-progress-linear
            :model-value="batchProgressPercent"
            :indeterminate="activeBatch.status === 'processing' && activeBatch.successCount === 0"
            color="primary"
            height="10"
            rounded
          />
          <p class="mt-2 text-body-2">
            <template v-if="activeBatch.status === 'processing'">
              {{ detail && detail.status === "notGenerated" ? "生成中" : "再生成中" }}... ({{ activeBatch.successCount }} / {{ activeBatch.totalCount }})
            </template>
            <template v-else-if="activeBatch.status === 'completed'">
              <v-icon size="small" color="success">mdi-check-circle</v-icon>
              {{ detail && detail.status === "notGenerated" ? "生成" : "再生成" }}が完了しました。
            </template>
            <template v-else-if="activeBatch.status === 'failed'">
              <v-icon size="small" color="red">mdi-alert-circle</v-icon>
              {{ detail && detail.status === "notGenerated" ? "生成" : "再生成" }}に失敗しました。
            </template>
          </p>
        </div>

        <div class="d-flex mt-4" style="gap: 0.5rem;">
          <v-spacer />
          <v-btn variant="text" @click="closeRegenerateDialog" :disabled="isStartingRegenerate">
            {{ activeBatch && activeBatch.status === "completed" ? "閉じる" : "キャンセル" }}
          </v-btn>
          <v-btn
            v-if="!activeBatch || activeBatch.status === 'failed'"
            color="primary"
            variant="flat"
            :loading="isStartingRegenerate"
            :disabled="startRegenerateDisabled"
            @click="startRegenerate"
          >
            {{ startButtonLabel }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import dayjs from "dayjs";
import PageTitle from "@/components/atoms/PageTitle.vue";
import AiReportStatusChip from "@/components/molecules/AiReportStatusChip.vue";
import { aiReportDetailModule } from "@/store/viewModules/ai-report/aiReportDetailModule";
import { AI_REPORT_LANGUAGES } from "@/api/ApiAiReport";
import type { AiReportLanguage, AiReportQuarter } from "@/api/ApiAiReport";

type RegenerateMode = "simple" | "translate";

interface DataType {
  year: number;
  quarter: AiReportQuarter;
  childId: number;
  regenerateDialogOpen: boolean;
  regenerateMode: RegenerateMode;
  selectedLanguage: AiReportLanguage | null;
  isStartingRegenerate: boolean;
  isPublishing: boolean;
  pollTimer: number | null;
}

export default defineComponent({
  components: {
    PageTitle,
    AiReportStatusChip,
  },
  data: (): DataType => ({
    year: 0,
    quarter: 1 as AiReportQuarter,
    childId: 0,
    regenerateDialogOpen: false,
    regenerateMode: "simple",
    selectedLanguage: null,
    isStartingRegenerate: false,
    isPublishing: false,
    pollTimer: null,
  }),
  computed: {
    detail() {
      return aiReportDetailModule.detail;
    },
    isFetching(): boolean {
      return aiReportDetailModule.isFetching;
    },
    activeBatch() {
      return aiReportDetailModule.activeBatch;
    },
    pageTitle(): string {
      return `VEVOレポート - 個別`;
    },
    breadcrumbItems(): Array<{ title: string; href?: string; color?: string }> {
      const name = this.detail
        ? `${this.detail.lastName} ${this.detail.firstName}`
        : `園児`;
      const monthsLabel = this.detail
        ? this.detail.monthsLabel
        : `第${this.quarter}クォーター`;
      return [
        { title: "VEVOレポート", href: "/ai-report", color: "primary" },
        {
          title: `${this.year}年度 ${monthsLabel}`,
          href: `/ai-report/quarter/${this.year}/${this.quarter}`,
          color: "primary",
        },
        { title: name },
      ];
    },
    canRegenerate(): boolean {
      if (!this.detail) return false;
      // 公開済は仕様により上書き不可。下書きor未作成のみ可。
      return this.detail.status !== "published";
    },
    noData(): boolean {
      // 対象クォーターに発達記録が1件もない場合は生成不可 (在籍児でもスキップ)。
      return !!this.detail && this.detail.dataCoverageMonths.length === 0;
    },
    isRegenerating(): boolean {
      return !!this.activeBatch && this.activeBatch.status === "processing";
    },
    contentLength(): number {
      return this.detail && this.detail.content ? this.detail.content.length : 0;
    },
    regenerateDialogTitle(): string {
      if (!this.detail) return "VEVOレポートを生成";
      return this.detail.status === "notGenerated"
        ? "VEVOレポートを生成"
        : "VEVOレポートを再生成";
    },
    batchProgressPercent(): number {
      const b = this.activeBatch;
      if (!b || b.totalCount === 0) return 0;
      return Math.floor((b.successCount / b.totalCount) * 100);
    },
    languageItems() {
      return AI_REPORT_LANGUAGES;
    },
    // 翻訳は「既存の本文を翻訳して上書き」する方式のため、本文がある下書きのみ選択可。
    canTranslate(): boolean {
      return !!this.detail && this.detail.status === "draft" && !!this.detail.content;
    },
    startRegenerateDisabled(): boolean {
      return this.regenerateMode === "translate" && this.selectedLanguage == null;
    },
    startButtonLabel(): string {
      if (this.regenerateMode === "translate") return "翻訳を開始";
      return this.detail && this.detail.status === "notGenerated"
        ? "生成を開始"
        : "再生成を開始";
    },
  },
  watch: {
    "$route.params": {
      handler() {
        this.readRouteParams();
        this.loadDetail();
      },
    },
  },
  beforeUnmount() {
    this.stopPolling();
  },
  async mounted() {
    this.readRouteParams();
    await this.loadDetail();
    // リロード時など、この園児の再生成がサーバ側で進行中なら進捗表示とポーリングを復元する
    await this.resumeRegenerateIfRunning();
  },
  methods: {
    readRouteParams() {
      this.year = parseInt(this.$route.params.businessYear as string, 10);
      this.quarter = parseInt(this.$route.params.quarter as string, 10) as AiReportQuarter;
      this.childId = parseInt(this.$route.params.childId as string, 10);
    },
    // 再生成バッチIDを園児×年度×クォーター単位でlocalStorageに保持する (リロード復元用)
    regenBatchKey(): string {
      return `ai_report_regen_batch_${this.childId}_${this.year}_${this.quarter}`;
    },
    saveRegenBatchId(batchId: number): void {
      try {
        window.localStorage.setItem(this.regenBatchKey(), String(batchId));
      } catch (e) {
        /* localStorage不可環境では復元なしで続行 */
      }
    },
    readRegenBatchId(): number | null {
      try {
        const v = window.localStorage.getItem(this.regenBatchKey());
        if (!v) return null;
        const n = parseInt(v, 10);
        return Number.isNaN(n) ? null : n;
      } catch (e) {
        return null;
      }
    },
    clearRegenBatchId(): void {
      try {
        window.localStorage.removeItem(this.regenBatchKey());
      } catch (e) {
        /* noop */
      }
    },
    async resumeRegenerateIfRunning() {
      const batchId = this.readRegenBatchId();
      if (batchId == null) return;
      try {
        const batch = await aiReportDetailModule.pollBatch({ batchId });
        if (batch && batch.status === "processing") {
          this.startPolling(batchId);
        } else {
          // 既に完了/失敗なら片付ける (詳細は再取得済み)
          this.clearRegenBatchId();
          aiReportDetailModule.setActiveBatch(null);
        }
      } catch (e) {
        // バッチ取得に失敗した場合は復元を諦める (localStorageは残し次回再試行)
      }
    },
    async loadDetail() {
      if (!this.year || !this.quarter || !this.childId) return;
      await aiReportDetailModule.loadDetail({
        businessYear: this.year,
        quarter: this.quarter,
        childId: this.childId,
      });
    },
    async setStatus(status: "published" | "draft") {
      if (!this.detail) return;
      const verb = status === "published" ? "公開する" : "下書きに戻す";
      const ok = window.confirm(
        `${this.detail.lastName} ${this.detail.firstName} さんのレポートを${verb}でよろしいですか？` +
          (status === "published" ? "\n保護者に通知が送信されます。" : "\n保護者からは見えなくなります。"),
      );
      if (!ok) return;
      this.isPublishing = true;
      try {
        await aiReportDetailModule.setStatus({
          businessYear: this.year,
          quarter: this.quarter,
          childId: this.childId,
          status,
        });
      } finally {
        this.isPublishing = false;
      }
    },
    async startRegenerate() {
      this.isStartingRegenerate = true;
      aiReportDetailModule.setActiveBatch(null);
      try {
        const language =
          this.regenerateMode === "translate" ? this.selectedLanguage : null;
        const batch = await aiReportDetailModule.regenerate({
          businessYear: this.year,
          quarter: this.quarter,
          childId: this.childId,
          language,
        });
        if (batch) {
          this.saveRegenBatchId(batch.batchId);
          this.startPolling(batch.batchId);
        }
      } finally {
        this.isStartingRegenerate = false;
      }
    },
    startPolling(batchId: number) {
      this.stopPolling();
      this.pollTimer = window.setInterval(async () => {
        const batch = await aiReportDetailModule.pollBatch({ batchId });
        if (batch && (batch.status === "completed" || batch.status === "failed")) {
          this.stopPolling();
          this.clearRegenBatchId();
          await this.loadDetail();
        }
      }, 1500);
    },
    stopPolling() {
      if (this.pollTimer !== null) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    closeRegenerateDialog() {
      this.regenerateDialogOpen = false;
      this.stopPolling();
      this.regenerateMode = "simple";
      this.selectedLanguage = null;
      aiReportDetailModule.setActiveBatch(null);
    },
    formatDateTime(value: string | null): string {
      if (!value) return "—";
      return dayjs(value).format("YYYY/MM/DD HH:mm");
    },
    formatCoverage(months: number[]): string {
      if (months.length === 0) return "なし";
      return `${months.join(", ")}月`;
    },
  },
});
</script>

<style lang="scss" scoped>
.ai-report-content {
  white-space: pre-wrap;
  line-height: 1.8;
  font-feature-settings: "palt";
}
</style>
